import { useMemo } from 'react';
import { X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LOADING_PLACEHOLDER } from '@/constants/app.constants';
import { DROPDOWN_ITEM_CLASS, DROPDOWN_PANEL_CLASS } from '@/styles/floatingPanel';

export interface SelectOption {
  id: number;
  name: string;
}

interface OptionSelectProps {
  options: readonly SelectOption[] | undefined;
  isLoading?: boolean;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string | undefined;
  className?: string;
  clearable?: boolean;
  'aria-invalid'?: boolean;
}

export function OptionSelect({
  options,
  isLoading,
  value,
  onChange,
  placeholder = 'Select',
  disabled,
  id,
  className,
  clearable,
  'aria-invalid': ariaInvalid,
}: OptionSelectProps) {
  const items = useMemo(
    () => options?.map((item) => ({ label: item.name, value: item.id })),
    [options],
  );
  const valueInOptions = options?.some((item) => item.id === value) ?? false;
  const safeValue = valueInOptions ? (value ?? null) : null;
  const showClear = clearable && !disabled && !isLoading && safeValue !== null;

  function handleClear(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    event.preventDefault();
    onChange(undefined);
  }

  return (
    <Select<number | null>
      items={items}
      value={safeValue}
      onValueChange={(next) => onChange(next ?? undefined)}
      disabled={disabled || isLoading}
    >
      <div className="relative">
        <SelectTrigger id={id} className={className} aria-invalid={ariaInvalid}>
          <SelectValue placeholder={isLoading ? LOADING_PLACEHOLDER : placeholder} />
          {showClear ? <span className="ml-2 size-5 shrink-0" aria-hidden /> : <SelectIcon />}
        </SelectTrigger>
        {showClear ? (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Clear selection"
            onClick={handleClear}
            className="absolute right-3 top-1/2 flex size-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-muted-foreground/70 transition-colors after:absolute after:-inset-2 hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <X className="size-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
      <SelectContent className={DROPDOWN_PANEL_CLASS}>
        {options && options.length > 0 ? (
          options.map((item) => (
            <SelectItem key={item.id} value={item.id} className={DROPDOWN_ITEM_CLASS}>
              {item.name}
            </SelectItem>
          ))
        ) : (
          <div className="px-3 py-2 text-sm text-muted-foreground text-center">
            {isLoading ? LOADING_PLACEHOLDER : 'No data found'}
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
